import React from 'react';
import { useTranslation } from 'react-i18next';
import { useGetEligiblePmsQuery } from '@/redux/internal-services/jobsApi';

/** JOB-FEAT-01 — renders in place of the generic RoleLookupSelect for the `project_manager_id`
 *  field on the `pm_assignment_required` gate's resolve panel (WorkflowCard.tsx), so a Project
 *  Manager is picked from the real eligible-PM-with-workload list (api-service
 *  app/domain/jobs/service.py JobService.list_eligible_pms) rather than a bare role-members
 *  dropdown with no workload signal. Same {value, onChange} shape as RoleLookupSelect so it
 *  slots into the same resolveValues plumbing. */
const PmAssignmentSelect: React.FC<{ engagementId: string; value: string; onChange: (v: string) => void }> = ({
  engagementId, value, onChange,
}) => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetEligiblePmsQuery(engagementId, { skip: !engagementId });
  const candidates = data ?? [];

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-xs text-ui-text focus:border-brand-dark focus:outline-none focus:ring-1 focus:ring-brand-dark"
    >
      <option value="">
        {isLoading ? t('common.loading', 'Loading…') : t('jobs.selectPm', 'Select a Project Manager…')}
      </option>
      {candidates.map((pm) => (
        <option key={pm.user_id} value={pm.user_id}>
          {pm.name}
          {' — '}
          {t('jobs.activeJobsCount', '{{count}} active job(s)', { count: pm.active_workload })}
          {pm.is_recommended ? ` (${t('jobs.recommended', 'recommended')})` : ''}
        </option>
      ))}
      {!isLoading && candidates.length === 0 && (
        <option value="" disabled>{t('jobs.noEligiblePms', 'No eligible Project Managers found')}</option>
      )}
    </select>
  );
};

export default PmAssignmentSelect;
